import { TIdVerificationPhone } from '@helpers/FundingRequirements/fundingRequirements.types';
import { logError, TAGS } from '@iwLogger/IwDataDogLogger';
import { fetchLendingAPIJSON } from '@iwoca/lapi-client/edge_de';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useStateKey } from '../../../../../../../../../../iwQueryClient/useStateKey';

type TAddPersonPhoneParams = {
    personUid: string;
    number: string;
};

// TODO: replace with the generated fetchPost... from @iwoca/lapi-client once it has
// POST /state/{state_key}/people/{person_uid}/phones/ (iwoca-django!69074).
const fetchPostPersonPhone = ({
    stateKey,
    personUid,
    number
}: TAddPersonPhoneParams & { stateKey: string }) =>
    fetchLendingAPIJSON<{ data: TIdVerificationPhone }>(
        `/api/lending/edge/state/${stateKey}/people/${personUid}/phones/`,
        { method: 'post', body: { data: { number } } }
    );

/**
 * Saves a new phone number onto a person, without a full customer state write.
 * Returns the saved phone, or the existing one if the person already has the number.
 */
export function useAddPersonPhone() {
    const stateKey = useStateKey();
    const queryClient = useQueryClient();

    const { mutateAsync, isLoading, isError, reset } = useMutation({
        mutationFn: async (params: TAddPersonPhoneParams) => {
            const { data } = await fetchPostPersonPhone({
                stateKey,
                ...params
            });
            return data;
        },
        onSuccess: () =>
            // The write bumps the customer state's version.
            queryClient.invalidateQueries({
                queryKey: ['customer_state', stateKey]
            }),
        onError: (error: Error) =>
            logError(
                `useAddPersonPhone: failed to add a phone number: ${error.message}`,
                { tags: TAGS.DE_LENDING }
            )
    });

    return {
        addPhone: mutateAsync,
        isAdding: isLoading,
        isAddError: isError,
        resetAddError: reset
    };
}
